import moment from 'moment'
import { OrderedMap } from 'immutable'
import { map, includes, isEmpty } from 'lodash-es'
import { intl } from 'lang/intl'

const commentState = {
  comments: OrderedMap(),
  isLoading: false,
  totalCount: 0,
  currentPage: 1,
  isInfiniteLoading: false,
  hasMoreElements: false,
  isWatchingRealtime: false,
  includeBotGreeting: false,
  hasSentFirstComment: false,
}

const initialState = {
  questions: {
    ...commentState,
  },
  chat: {
    ...commentState,
  },
  shouldScroll: true,
  scrollIntention: false,
}

const getQuestionsAsOrderedMap = (questions) => {
  // we want an ordered list, thus not using keyBy
  const questionsByKey = map(questions, (question) => {
    return [question.comment_id.toString(), question]
  })

  return OrderedMap(questionsByKey)
}

const hasMoreElements = (comments, action) => comments.size < action.totalCount

const mergeState = (actionName, state, newState) => {
  const key = includes(actionName, 'QUESTION') ? 'questions' : 'chat'

  return {
    ...state,
    [key]: {
      ...state[key],
      ...newState,
    },
  }
}

const addSingleComment = (action, state, key) => {
  const newComments = getQuestionsAsOrderedMap([action.comment])
  // Push from front
  return newComments.concat(state[key].comments)
}

const generateBotGreeting = (includeBotGreeting, commentType, webinarTitle) => {
  if (commentType === 'questions' || !includeBotGreeting) {
    return OrderedMap()
  }

  const commentId = 'botGreeting'

  return OrderedMap({
    [commentId]: {
      comment_id: commentId,
      comment_type: 'chat',
      content_text: intl.formatMessage({
        defaultMessage:
          'Hi! There are no chat messages posted yet. Chat messages can be seen by everyone, including the audience of the webinar.',
        id: 'IPUd++',
      }),
      name: intl.formatMessage(
        {
          defaultMessage: 'Welcome to {title}',
          id: '0u2ePg',
        },
        { title: webinarTitle }
      ),
      truncated_name: '',
      entry_date_ansi: moment().format('YYYY-MM-DD HH:mm:ss'),
    },
  })
}

const injectFirstMessage = (injectFirstMessageId, commentType, collection) => {
  if (!injectFirstMessageId) {
    return collection
  }

  return OrderedMap({
    [injectFirstMessageId]: {
      comment_id: injectFirstMessageId,
      comment_type: commentType,
      content_text: '',
      name: '',
      entry_date_ansi: moment().format('YYYY-MM-DD HH:mm:ss'),
    },
  }).concat(collection)
}

export default ({ actionNames, injectFirstMessageId }) =>
  (state = initialState, action = undefined) => {
    const key = includes(action.type, 'QUESTION') ? 'questions' : 'chat'

    switch (action.type) {
      case actionNames.INIT_QUESTIONS:
      case actionNames.INIT_CHAT: {
        return mergeState(action.type, state, {
          isLoading: !action.doNotFetchQuestions,
          presenterToken: action.presenterToken,
          includeBotGreeting: action.includeBotGreeting,
        })
      }
      case actionNames.FETCH_QUESTIONS_SUCCESS:
      case actionNames.FETCH_CHAT_SUCCESS: {
        if (isEmpty(action.comments) && parseInt(action.totalCount, 10) === 0) {
          return mergeState(action.type, state, {
            comments: injectFirstMessage(
              injectFirstMessageId,
              key,
              generateBotGreeting(
                state[key].includeBotGreeting,
                key,
                action.webinarTitle
              )
            ),
            isLoading: false,
            totalCount: action.totalCount,
            hasMoreElements: false,
          })
        }

        const newComments = getQuestionsAsOrderedMap(action.comments)
        // Push from front
        const comments = newComments.concat(state[key].comments)

        return mergeState(action.type, state, {
          comments: injectFirstMessage(injectFirstMessageId, key, comments),
          isLoading: false,
          totalCount: action.totalCount,
          hasMoreElements: hasMoreElements(comments, action),
        })
      }
      case actionNames.FETCH_QUESTIONS_FAILURE:
      case actionNames.FETCH_CHAT_FAILURE: {
        return mergeState(action.type, state, {
          isLoading: false,
        })
      }
      case actionNames.INFINITE_LOAD_QUESTIONS_SUCCESS:
      case actionNames.INFINITE_LOAD_CHAT_SUCCESS: {
        const newComments = getQuestionsAsOrderedMap(action.comments)
        // Push from back
        const comments = state[key].comments.concat(newComments)

        return mergeState(action.type, state, {
          comments,
          totalCount: action.totalCount,
          isInfiniteLoading: false,
          currentPage: action.page,
          hasMoreElements: hasMoreElements(comments, action),
        })
      }
      case actionNames.INFINITE_LOAD_QUESTIONS_FAILURE:
      case actionNames.INFINITE_LOAD_CHAT_FAILURE: {
        return mergeState(action.type, state, {
          isInfiniteLoading: false,
        })
      }
      case actionNames.RESET_QUESTIONS:
      case actionNames.RESET_CHAT: {
        return mergeState(action.type, state, initialState[key])
      }
      case actionNames.DELETE_QUESTION:
      case actionNames.DELETE_CHAT_MESSAGE: {
        const comments = state[key].comments.delete(action.commentId)

        return mergeState(action.type, state, {
          comments,
        })
      }
      case actionNames.INFINITE_LOAD_QUESTIONS:
      case actionNames.INFINITE_LOAD_CHAT: {
        return mergeState(action.type, state, {
          isInfiniteLoading: true,
        })
      }
      case actionNames.REALTIME_ADD_QUESTION:
      case actionNames.REALTIME_ADD_CHAT_MESSAGE: {
        return mergeState(action.type, state, {
          comments: addSingleComment(action, state, key),
        })
      }
      case actionNames.REALTIME_REMOVE_COMMENT: {
        const { commentId: commentIdInt } = action
        const commentId = commentIdInt.toString()

        if (state.questions.comments.has(commentId)) {
          const comments = state.questions.comments.delete(commentId)

          return {
            ...state,
            questions: {
              ...state.questions,
              comments,
            },
          }
        }

        if (state.chat.comments.has(commentId)) {
          const comments = state.chat.comments.delete(commentId)

          return {
            ...state,
            chat: {
              ...state.chat,
              comments,
            },
          }
        }

        return state
      }
      case actionNames.FETCH_SINGLE_QUESTION_SUCCESS:
      case actionNames.FETCH_SINGLE_CHAT_MESSAGE_SUCCESS: {
        return mergeState(action.type, state, {
          comments: addSingleComment(action, state, key),
        })
      }
      case actionNames.OPTIMISTICALLY_ADD_QUESTION:
      case actionNames.OPTIMISTICALLY_ADD_CHAT_MESSAGE: {
        return mergeState(action.type, state, {
          comments: addSingleComment(action, state, key),
        })
      }
      case actionNames.ADD_CHAT_MESSAGE: {
        return mergeState(action.type, state, {
          hasSentFirstComment: true,
        })
      }
      case actionNames.WATCH_REALTIME_QUESTIONS:
      case actionNames.WATCH_REALTIME_CHAT: {
        return mergeState(action.type, state, {
          isWatchingRealtime: true,
        })
      }
      case actionNames.SET_SCROLLING_INTENTION: {
        return {
          ...state,
          scrollIntention: action.scrollIntention,
        }
      }
      case actionNames.SET_SHOULD_SCROLL: {
        return {
          ...state,
          shouldScroll: action.shouldScroll,
        }
      }
      case actionNames.RESET_SCROLLING_PROPS: {
        return {
          ...state,
          scrollIntention: false,
          shouldScroll: false,
        }
      }
      case actionNames.OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS: {
        const { comments } = action

        return {
          ...state,
          questions: {
            ...state.questions,
            comments: getQuestionsAsOrderedMap(comments),
          },
        }
      }
      default:
        return state
    }
  }
